import { useEffect, useState } from "react";
import { Accordion } from "../../../components/atomic/Accordion";
import ActivityTimeline from "../../../components/atomic/ActivityTimeline";
import MobileDetailsSection from "../../../components/composed/MobileDetailsSection";
import { LoanApplicationService } from "../../../services/LoanApplication";
import {
  MOBILE_LOAN_DETAILS_SECTION,
  MOBILE_LOAN_DETAILS_SECTION_LINKS
} from "../../../services/LoanApplication/constants";
import { CheckmarkIcon, ErrorIcon } from "react-hot-toast";
import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import GetAuthContext from "../../../../../src/shared/context/AuthContext";
import { ORG_IDS } from "../../../shared/utils/constants";

interface MobileSectionData {
  loanDetails: any;
  loanID: string;
  pageName: string;
  token: string;
  isLenderDashboard: boolean;
  whiteLabelStyle: any;
}

const MobileSection = (data: MobileSectionData) => {
  const { userState } = GetAuthContext();
  const { organizationID } = userState || {};

  const [page, setPage] = useState(1);
  const [loanActivity, setLoanActivity] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [kycDocs, setKycDocs] = useState([]);
  const rejectedDocs: any = [];
  const approvedDocs: any = [];

  async function getLoanActivity(page: number) {
    const response = (await LoanApplicationService.getLoanActivity({
      loanApplicationID: data.loanID as string,
      token: data.token,
      page,
      pageName: data.pageName,
      isLenderDashboard: data.isLenderDashboard
    })) as any;
    if (response.status) {
      if (response.data?.userActivityHistory?.length > 0) {
        setLoanActivity((prevState) => [
          ...prevState,
          ...response.data.userActivityHistory
        ]);
      } else {
        setHasMorePages(false);
      }
    } else {
      console.log("API failed", response.error);
    }
  }

  function onViewMoreClick() {
    setPage((prevState) => prevState + 1);
  }

  useEffect(() => {
    if (window.innerWidth < 640) {
      getLoanActivity(1);
      getKYCData();
    }
  }, []);

  useEffect(() => {
    if (page > 1 && hasMorePages) {
      getLoanActivity(page);
    }
  }, [page]);

  async function getKYCData() {
    const response = (await LoanApplicationService.getLoanKYC({
      loanApplicationID: data.loanID as string,
      token: data.token,
      pageName: data.pageName,
      isLenderDashboard: data.isLenderDashboard
    })) as any;

    if (response.status) {
      setKycDocs(response.data?.kycDocs ?? []);
    }
  }

  kycDocs.map((doc: any) =>
    doc.rejectionReason?.length > 0
      ? rejectedDocs.push(doc)
      : approvedDocs.push(doc)
  );

  // Don't render this component for MFL LAP organization
  if (organizationID === ORG_IDS.MFL_LAP) {
    return null;
  }

  return (
    <>
      {/* Mobile Section */}

      <div className="block md:hidden">
        <Accordion
          title="Application Details"
          styles={{
            container: "block border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
          defaultOpen={true}
        >
          <MobileDetailsSection
            details={data.loanDetails}
            title={"User Details"}
            levelsData={MOBILE_LOAN_DETAILS_SECTION}
            additionalLinks={MOBILE_LOAN_DETAILS_SECTION_LINKS}
          />
        </Accordion>

        <Accordion
          title="Activity Timeline"
          styles={{
            container: "border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
          defaultOpen={false}
        >
          <div className="px-4">
            <ActivityTimeline
              page={page}
              whiteLabelStyle={data.whiteLabelStyle}
              timeline={loanActivity}
              hasMorePages={hasMorePages}
              onViewMoreClick={onViewMoreClick}
            />
          </div>
        </Accordion>

        <Accordion
          title="KYC Docs"
          styles={{
            container: "border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
          defaultOpen={false}
        >
          <div className="flex flex-wrap px-4 pt-4">
            <div className="w-full pb-8">
              {!kycDocs?.length ? (
                <div className="text-xs text-gray-400">
                  No documents uploaded yet!
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="text-sm text-gray-700 font-medium w-max border-b-2 border-indigo-400 py-2 pr-2 mb-4">
                      Approved
                    </h4>
                    <div className="flex flex-wrap">
                      {approvedDocs?.length > 0 ? (
                        approvedDocs.map((doc: any) => {
                          return (
                            <div className="w-1/2 px-1 text-xs flex items-center justify-start mb-4">
                              <CheckmarkIcon className="w-4 h-4 text-gray-400 mr-4" />
                              <span className="truncate w-[calc(100%_-_40px)]">
                                {doc.docType.replace(/_/g, " ")}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs mt-4 text-gray-400">None</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm text-gray-700 font-medium w-max border-b-2 border-indigo-400 py-2 pr-2 mb-4">
                      Rejected
                    </h4>
                    <div className="flex flex-wrap">
                      {rejectedDocs.length > 0 ? (
                        rejectedDocs.map((doc: any) => {
                          return (
                            <div className="w-full px-1 text-xs flex items-center justify-start mb-4">
                              <ErrorIcon className="w-4 h-4 text-red-500 mr-4" />
                              <span className="truncate w-[calc(100%_-_120px)]">
                                {doc.docType.replace(/_/g, " ")}
                              </span>
                              <div className="flex items-center">
                                {doc.frontImagePath && (
                                  <ArrowDownTrayIcon
                                    onClick={() =>
                                      window.open(doc.frontImagePath, "_self")
                                    }
                                    className="w-4 h-4 text-gray-400 mr-4"
                                  />
                                )}
                                {doc.backImagePath && (
                                  <ArrowDownTrayIcon
                                    onClick={() =>
                                      window.open(doc.backImagePath, "_self")
                                    }
                                    className="w-4 h-4 text-gray-400 mr-4"
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs mt-4 text-gray-400">None</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Accordion>
      </div>
    </>
  );
};

export default MobileSection;
